import { Injectable } from '@angular/core';

import { parseISO } from 'date-fns';
import {
    BaseFieldDefinition,
    BaseNestedFieldDefinition,
    BaseNestedFieldDefinitionGuard,
    DottedKey,
    FieldType
} from '../../../core/field-definitions/entities/field-definition.entity';
import { Family, MergeStrategy } from '../../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { setAny } from '../../../core/records/helpers/deep-get-set.helper';
import { FieldValue, SimpleFieldValue } from '../../entities/field-values/field-type-values.entity';
import { GenericTypedValue } from '../../entities/field-values/generic-typed-value.entity';
import {
    assertBooleanOrNull,
    assertDateOrNull,
    assertNumberOrNull,
    assertStringOrNull,
    isDate
} from './field-validation.helper';

type FieldWriteFunction<F extends PartialRecord> = (
    def: BaseNestedFieldDefinition,
    record: F,
    fieldValue: any,
    mergeStrategy: MergeStrategy
) => F;

@Injectable({ providedIn: 'root' })
export class FieldWriteHelper {
    private readonly FieldWriteMapping: Record<FieldType, FieldWriteFunction<any>> = {
        [FieldType.BooleanField]: this.writeBooleanField,
        [FieldType.ComplexField]: this.writeComplexField,
        [FieldType.ComplexListField]: this.writeComplexListField,
        [FieldType.ComplexMapField]: this.writeComplexField,
        [FieldType.DateField]: this.writeDateField,
        [FieldType.DoubleField]: this.writeDoubleField,
        [FieldType.EDTFField]: this.writeEDTFField,
        [FieldType.EnumField]: this.writeEnumField,
        [FieldType.EnumMapField]: this.writeUnsupported,
        [FieldType.FieldDefinitionReferenceField]: this.writeSimpleField,
        [FieldType.FramesField]: this.writeFramesField,
        [FieldType.GeoCoordinateField]: this.writeUnsupported,
        [FieldType.HiddenField]: this.writeUnsupported,
        [FieldType.ListField]: this.writeListField,
        [FieldType.LongField]: this.writeLongField,
        [FieldType.MapField]: this.writeMapField,
        [FieldType.MultiItemField]: this.writeMultiItemField,
        [FieldType.OrderedMultiItemField]: this.writeUnsupported,
        [FieldType.PositionField]: this.writePositionField,
        [FieldType.SimpleField]: this.writeSimpleField,
        [FieldType.TextField]: this.writeTextField,
        [FieldType.ThesaurusField]: this.writeThesaurusField,
        [FieldType.ThesaurusLabelField]: this.writeUnsupported,
        [FieldType.ThesaurusUriField]: this.writeUnsupported,
        [FieldType.TimeCodeField]: this.writeTimeCodeField,
        [FieldType.TimerField]: this.writeTimerField,
        [FieldType.UrlField]: this.writeUrlField,
        [FieldType.VectorField]: this.writeVectorField
    };

    /*
        Methods use Generics <F extends PartialRecord> instead of the PartialRecord interface.
        This is to ensure the return type is the same as the input type (and we don't lose class specificity).
        When using the PartialRecord interface, the calling class just gets a generic PartialRecord back.
    */
    public writeField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        value: any,
        mergeStrategy: MergeStrategy = MergeStrategy.OVERWRITE
    ): F | undefined {
        if (typeof value === 'undefined') {
            console.error(def.FlatKey, 'value is undefined. Field will be ignored.');
            return undefined;
        }

        return this.FieldWriteMapping[def.Type].call(this, def, record, value, mergeStrategy);
    }

    // #region Writers

    private writeUnsupported<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ) {
        console.error('Writing to', def.Type, 'not supported');
        return undefined;
    }

    private writeSimpleField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertStringOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeTextField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertStringOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeBooleanField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: boolean | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertBooleanOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeTimeCodeField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertStringOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeDateField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ): F {
        const dateValue = fieldValue ? parseISO(fieldValue) : fieldValue;
        assertDateOrNull(dateValue);

        const stringValue: string | null = isDate(dateValue) ? dateValue.toISOString() : null;

        return this.setSimplePropertyFormValue(record, def, stringValue, mergeStrategy);
    }

    private writeEDTFField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertStringOrNull(fieldValue);

        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeLongField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: number | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertNumberOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeFramesField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: number | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertNumberOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeDoubleField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: number | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertNumberOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeEnumField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValues: (string | null)[],
        mergeStrategy: MergeStrategy
    ): F {
        // MED-5234: Ignore undefined values
        if (fieldValues.every(value => typeof value === 'undefined')) {
            return record;
        }

        fieldValues.forEach(fieldValue => assertStringOrNull(fieldValue));
        return this.setSimplePropertyFormValue(record, def, fieldValues, mergeStrategy);
    }

    private writeUrlField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: string | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertStringOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeVectorField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: number[] | null,
        mergeStrategy: MergeStrategy
    ): F {
        assertStringOrNull(fieldValue);
        return this.setSimplePropertyFormValue(record, def, fieldValue, mergeStrategy);
    }

    private writeListField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        simpleTypeValues: SimpleFieldValue[],
        mergeStrategy: MergeStrategy
    ): F {
        if (
            def.Type === FieldType.ListField &&
            BaseNestedFieldDefinitionGuard(def) &&
            def.Children &&
            def.Children.length > 0
        ) {
            const key: string = def.Children[0].Key;

            const converted: GenericTypedValue<SimpleFieldValue>[] = simpleTypeValues.map(simpleValue => ({
                type: key,
                value: simpleValue
            }));

            return this.writeMultiItemField(def, record, converted, mergeStrategy);
        } else {
            console.error('definition is not a ListField, or does not have any children');
        }

        return record;
    }

    private writeMultiItemField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        typedValues: GenericTypedValue<FieldValue>[],
        mergeStrategy: MergeStrategy
    ): F {
        const fieldValues: Record<string, FieldValue[]> = this.typedValuesToMetadataValues(typedValues);
        this.setPropertyValue(record, def.DottedKey, fieldValues, mergeStrategy);

        return record;
    }

    private writePositionField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        typedValues: GenericTypedValue<string>[],
        mergeStrategy: MergeStrategy
    ): F {
        const fieldValues: Record<string, FieldValue[]> = this.typedValuesToMetadataValues(typedValues);
        const singularFieldValues: Record<string, FieldValue> = {};

        // Only take first values of each key
        Object.keys(fieldValues).forEach(key => {
            const values: FieldValue[] = fieldValues[key];

            if (values.length > 0) {
                singularFieldValues[key] = values[0];
            }
        });

        // MED-3608 location data unreadable by components after save
        this.setPropertyValue(record, def.DottedKey, singularFieldValues, mergeStrategy);

        return record;
    }

    private writeThesaurusField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValue: FieldValue,
        mergeStrategy: MergeStrategy
    ): F {
        return this.setThesaurusPropertyFormValue(record, def.DottedKey, fieldValue, mergeStrategy);
    }

    private writeMapField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValues: FieldValue | FieldValue[],
        mergeStrategy: MergeStrategy
    ): F {
        if (
            def.Type === FieldType.MapField &&
            BaseNestedFieldDefinitionGuard(def) &&
            def.Children &&
            def.Children.length > 0
        ) {
            let value: FieldValue = Array.isArray(fieldValues) ? fieldValues[0] : fieldValues;
            this.setPropertyValue(record, def.DottedKey, value, mergeStrategy);
        } else {
            console.error('definition is not a MapField, or does not have any children');
        }

        return record;
    }

    private writeTimerField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValues: FieldValue | FieldValue[],
        mergeStrategy: MergeStrategy
    ): F {
        if (
            def.Type === FieldType.TimerField &&
            BaseNestedFieldDefinitionGuard(def) &&
            def.Children &&
            def.Children.length > 0
        ) {
            let value: FieldValue = Array.isArray(fieldValues) ? fieldValues[0] : fieldValues;
            this.setPropertyValue(record, def.DottedKey, value, mergeStrategy);
        } else {
            console.error('definition is not a MapField, or does not have any children');
        }

        return record;
    }

    private writeComplexField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValues: FieldValue | FieldValue[],
        mergeStrategy: MergeStrategy
    ): F {
        if (
            (def.Type === FieldType.ComplexField || def.Type === FieldType.ComplexMapField) &&
            BaseNestedFieldDefinitionGuard(def) &&
            def.Children &&
            def.Children.length > 0
        ) {
            let value: FieldValue = Array.isArray(fieldValues) ? fieldValues[0] : fieldValues;
            this.setPropertyValue(record, def.DottedKey, value, mergeStrategy);
        } else {
            console.error('definition is not a ComplexField, or does not have any children');
        }

        return record;
    }

    private writeComplexListField<F extends PartialRecord>(
        def: BaseNestedFieldDefinition,
        record: F,
        fieldValues: FieldValue[],
        mergeStrategy: MergeStrategy
    ): F {
        if (
            def.Type === FieldType.ComplexListField &&
            BaseNestedFieldDefinitionGuard(def) &&
            def.Children?.length === 1
        ) {
            const key: string = def.Children[0].Key;

            const converted: GenericTypedValue<FieldValue>[] = fieldValues.map(fieldValue => ({
                type: key,
                value: fieldValue
            }));

            return this.writeMultiItemField(def, record, converted, mergeStrategy);
        } else {
            console.error('definition is not a ComplexListField, or does not have exactly one child');
        }

        return record;
    }
    // #endregion

    // #region Helpers
    private setThesaurusPropertyFormValue<F extends PartialRecord>(
        record: F,
        dottedKey: DottedKey,
        fieldValue: FieldValue,
        mergeStrategy: MergeStrategy
    ): F {
        const singleFieldValue: FieldValue = fieldValue instanceof Array ? fieldValue[0] : fieldValue;

        return this.setPropertyValue(record, dottedKey, singleFieldValue, mergeStrategy);
    }

    private setSimplePropertyFormValue<F extends PartialRecord>(
        record: F,
        def: BaseFieldDefinition,
        value: SimpleFieldValue | SimpleFieldValue[],
        mergeStrategy: MergeStrategy
    ): F {
        const simpleTypeValue: SimpleFieldValue = value instanceof Array ? value[0] : value;

        return this.setPropertyValue(record, def.DottedKey, simpleTypeValue, mergeStrategy);
    }

    private setPropertyValue<F extends PartialRecord>(
        record: F,
        dottedKey: DottedKey,
        value: FieldValue,
        mergeStrategy: MergeStrategy = MergeStrategy.OVERWRITE
    ): F {
        const path: string[] = dottedKey.split('.');
        setAny(record.metadata, value as any, ...path);
        this.addMergeStrategy(record, dottedKey, mergeStrategy);

        return record;
    }

    private typedValuesToMetadataValues(typedValues: GenericTypedValue<FieldValue>[]): Record<string, FieldValue[]> {
        const metadataValues: Record<string, FieldValue[]> = {};

        if (Object.keys(typedValues).length === 0) {
            return metadataValues;
        }

        for (const typedValue of typedValues) {
            const type: string = typedValue.type;
            const value: FieldValue = typedValue.value;

            const values: FieldValue[] = metadataValues[type] || [];
            if (!values.includes(value)) {
                values.push(value);
            }
            metadataValues[type] = values;
        }

        return metadataValues;
    }

    private addMergeStrategy(
        record: PartialRecord,
        dottedKey: DottedKey,
        strategy: MergeStrategy = MergeStrategy.OVERWRITE
    ): void {
        // MED-4552: Always set merge strategy on top level
        const path: string[] = dottedKey.split('.');
        const topFieldPath: string[] = path.slice(1, 2);
        setAny(record.metadata, strategy, Family.MergeStrategies, ...topFieldPath);
    }

    // #endregion
}
